<template>
  <div>
    <div v-for="(dialog, index) in popups" :key="index">
      <v-dialog
        v-model="dialog.isVisible"
        persistent
        :max-width="typeof dialog.width === 'undefined' ? 500 : dialog.width"
        :width="dialog.width"
      >
        <div class="dialog">
          <div class="dialog-header _warning">
            <div class="dialog-header__logo">
              <img alt="XTUR Sp. z o.o." src="@/assets/images/layout/logo-page-white.svg" />
            </div>
            <div class="dialog-header__text">
              <div>
                <v-icon size="37">$dialogWarning</v-icon>
                <span>
                  {{ dialog.title }}
                </span>
              </div>
            </div>
          </div>
          <div class="dialog-body">
            <div>
              <div v-for="(text, num) in dialog.content.split('\n')" :key="num">
                <span v-html="text" />
              </div>
              <div v-if="dialog.type == 'cost-document-popup-table'">
                <cost-document-popup-table :cost-document-settled-result="dialog.object" @select="select">
                </cost-document-popup-table>
              </div>
              <div v-else-if="dialog.type == 'cost-document-virtual-popup-table'">
                <cost-document-virtual-popup-table
                  :related-documents="dialog.object"
                  @select="selectRelatedDocuments"
                />
              </div>
            </div>
          </div>
          <div class="dialog-actions">
            <div v-if="!hasCustomButtons(dialog)" class="dialog-actions-custom">
              <v-spacer v-if="!dialog.customButtons.filter((x) => typeof x.insertSpace != 'undefined').length > 0" />
              <template v-for="(button, key) in customButtonsArray(dialog)">
                <v-btn
                  :key="button.text"
                  class="dialog-btn"
                  small
                  elevation="0"
                  :outlined="typeof button.outlined !== 'undefined' && button.outlined"
                  :text="typeof button.outlined !== 'undefined' && button.outlined"
                  :color="typeof button.color !== 'undefined' ? button.color : null"
                  @click="clickCustomButton(dialog, button)"
                >
                  {{ button.text }}
                </v-btn>
                <v-spacer v-if="button.insertSpace" :key="key" />
              </template>
            </div>
            <v-spacer v-if="hasCustomButtons(dialog)" />
            <v-btn
              v-if="hasCustomButtons(dialog)"
              class="dialog-btn"
              small
              elevation="0"
              color="warning"
              outlined
              text
              @click="clickCancel(dialog)"
            >
              {{ $t("cancel") | capitalize }}
            </v-btn>
            <v-btn
              v-if="hasCustomButtons(dialog)"
              class="dialog-btn"
              small
              elevation="0"
              color="warning"
              @click="clickAccept(dialog)"
            >
              {{ $t("ok") | capitalize }}
            </v-btn>
          </div>
        </div>
      </v-dialog>
    </div>
  </div>
</template>

<script lang="ts">
import "reflect-metadata";
import { Component, Vue } from "vue-property-decorator";
import { ConfirmCustomButton, ConfirmModel } from "@/models/components/popup/Confirm";
import { Resolver } from "@/models/components/popup/Resolver";
import { Rejecter } from "@/models/components/popup/Rejecter";
import IsVisible from "@/models/components/popup/IsVisible";
import CostDocumentPopupTable from "./ConfirmPopupComponents/CostDocumentPopupTable.vue";
import { CostDocumentItemsPairSimple } from "@/models/api/costDocuments/CostDocumentItemsPairs";
import CostDocumentVirtualPopupTable from "./ConfirmPopupComponents/CostDocumentVirtualPopupTable.vue";
import { RelatedDocument } from "@/models/api/costDocuments/RelatedDocument";

@Component({
  components: {
    CostDocumentPopupTable,
    CostDocumentVirtualPopupTable,
  },
})
export default class ConfirmPopup extends Vue {
  get popups(): Array<ConfirmModel & Resolver & Rejecter & IsVisible> {
    return this.$store.direct.state.popup.confirms;
  }

  hasCustomButtons(popup: ConfirmModel): boolean {
    return typeof popup == "undefined" || popup.customButtons === null;
  }

  customButtonsArray(popup: ConfirmModel): Array<ConfirmCustomButton> {
    if (this.hasCustomButtons(popup)) return [];
    return popup.customButtons ?? [];
  }

  clickAccept(popup: ConfirmModel & Resolver & IsVisible): void {
    popup.resolver(true);
    popup.isVisible = false;
  }

  clickCancel(popup: ConfirmModel & Rejecter & IsVisible): void {
    popup.rejecter();
    popup.isVisible = false;
  }

  clickCustomButton(popup: ConfirmModel & Resolver & IsVisible, button: ConfirmCustomButton): void {
    if (popup.type == "cost-document-popup-table") {
      popup.resolver({
        selectedItems: this.selectedCostDocument,
        value: button.value,
      });
    } else if (popup.type == "cost-document-virtual-popup-table") {
      popup.resolver({
        selectedItems: this.selectedRelatedDocuments,
        value: button.value,
      });
    } else {
      popup.resolver(button.value);
    }
    popup.isVisible = false;
  }

  selectedCostDocument: number[] = [];
  selectedRelatedDocuments: string[] = [];

  select(value: CostDocumentItemsPairSimple[]) {
    this.selectedCostDocument = value.map((x) => x.id);
  }

  selectRelatedDocuments(value: RelatedDocument[]) {
    this.selectedRelatedDocuments = value.map((x) => x.id);
  }
}
</script>

<style lang="scss">
.v-dialog {
  .v-card.v-card--flat {
    padding: 0;
  }
}

.dialog {
  width: 100%;
  height: auto;
  min-height: 290px;
  background-color: white;
  border-radius: 10px;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.dialog-header {
  position: relative;
  height: 110px;
}

.dialog-header::before {
  content: "";
  position: absolute;
  height: 110px;
  width: calc(100% + 300px);
  top: 0;
  left: -150px;
  border-radius: 0 0 50% 50% / 0 0 3em 3em;
  display: block;
  background-color: white;
}

.dialog-header._error::before {
  background-color: $error;
}
.dialog-header._warning::before {
  background-color: $warning;
}
.dialog-header._success::before {
  background-color: $success;
}

.dialog-header__text {
  color: white;
  display: block;
  position: absolute;
  left: 50%;
  top: 50%;
  -webkit-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);
  font-size: 20px;
  font-weight: 600;
}

.dialog-header__text span {
  vertical-align: middle;
}

.dialog-header__logo {
  position: absolute;
  top: 43px;
  left: 23px;
}

.dialog-body {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  padding: 20px 20px 10px 20px;
  span {
    color: black;
    font-size: 13px;
    word-wrap: break-word;
    ul {
      margin-top: 3px;
      margin-bottom: 12px;
    }
  }
}

.dialog-actions {
  display: flex;
  flex-direction: row;
  gap: 10px;
  padding: 10px 20px 20px 20px;
  height: auto;
}

.dialog-btn.v-btn--outlined {
  border: thin solid $input-border !important;
}

.dialog-actions-custom {
  display: flex;
  width: 100%;
  gap: 10px;
}
</style>
